import React, { useState } from 'react';
import { MapPin, TrainFront, Megaphone, Route, ShieldAlert, Clock } from 'lucide-react';
import SourcesTag from './SourcesTag';
import { addMinutes, parseFinish, readPlannerSettings } from './raceTime';
import type { Race, SpectatorGuide, Wave } from './types';

const MARATHON_MILES = 26.2;

/** Where to watch, how to hop between spots, and when the runner should pass each one. */
const SpectatorGuideView: React.FC<{ race: Race; guide: SpectatorGuide; waves: Wave[] }> = ({
  race,
  guide,
  waves,
}) => {
  const saved = readPlannerSettings(race.id);
  const [waveId, setWaveId] = useState(() => saved.waveId ?? waves[0].id);
  const [finish, setFinish] = useState(() => saved.finish ?? '');

  const wave = waves.find((w) => w.id === waveId) ?? waves[0];
  const finishMin = parseFinish(finish);
  const crossing = addMinutes(wave.time, guide.startCrossingMin);
  const etaFor = (mile: number) =>
    finishMin ? addMinutes(wave.time, guide.startCrossingMin + (finishMin * mile) / MARATHON_MILES) : null;

  return (
    <section className="ev-section">
      <p className="ev-sub">{guide.lede}</p>

      <div className="spec-eta-bar">
        <Clock size={15} />
        <label className="calc-field">
          <span>Runner’s wave</span>
          <select value={waveId} onChange={(e) => setWaveId(e.target.value)}>
            {waves.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label} · {w.time}
              </option>
            ))}
          </select>
        </label>
        <label className="calc-field">
          <span>Goal finish</span>
          <div className="calc-input-wrap">
            <input
              type="text"
              inputMode="numeric"
              placeholder="3:30"
              value={finish}
              onChange={(e) => setFinish(e.target.value)}
            />
            <em>h:mm</em>
          </div>
        </label>
        <p className="spec-eta-note">
          {finishMin
            ? `Even pace, crossing the start ~${crossing}. Leave 20–35 min between stops.`
            : 'Enter a goal time to see when your runner passes each spot.'}
        </p>
      </div>

      <ol className="spec-spots">
        {guide.spots.map((s) => (
          <li key={s.name} className={`spec-spot ${s.cheerZone ? 'spec-spot--zone' : ''}`}>
            <div className="spec-spot-mile">
              <strong>{s.mile}</strong>
              <span>mi · {(s.mile * 1.609).toFixed(1)} km</span>
              {etaFor(s.mile) && <em className="spec-spot-eta">~{etaFor(s.mile)}</em>}
            </div>
            <div className="spec-spot-body">
              <h3>
                {s.name}
                {s.cheerZone && (
                  <span className="spec-zone-badge">
                    <Megaphone size={10} /> {s.cheerZone}
                  </span>
                )}
              </h3>
              <p className="spec-spot-where">
                <MapPin size={11} /> {s.where}
              </p>
              <p className="spec-spot-transit">
                <TrainFront size={11} /> {s.transit}
              </p>
              <p className="spec-spot-why">{s.why}</p>
              {s.downside && <p className="spec-spot-down">{s.downside}</p>}
            </div>
          </li>
        ))}
      </ol>

      <div className="spec-routes">
        {guide.routes.map((r) => (
          <div key={r.title} className="spec-route">
            <h3>
              <Route size={14} /> {r.title}
            </h3>
            <ol>
              {r.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {r.note && <p className="spec-route-note">{r.note}</p>}
          </div>
        ))}
      </div>

      <div className="fuel-panel spec-rules">
        <div className="fuel-panel-head">
          <ShieldAlert size={16} />
          <h3>Rules &amp; logistics</h3>
          <span className="fuel-panel-tag">spectators</span>
        </div>
        <ul className="fuel-panel-list">
          {guide.rules.map((r) => (
            <li key={r.label}>
              <span className="fuel-panel-key">{r.label}</span>
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
        <div className="spec-sources">
          <SourcesTag refs={guide.sources} label="Sources" />
        </div>
      </div>
    </section>
  );
};

export default SpectatorGuideView;
